/**
 * Patient exam sharing.
 *
 * Two audiences in one file, kept visibly apart:
 *
 * - the clinician hooks below use the app's authenticated axios instance
 * - the patient helpers at the bottom run on the public /shared/:token page,
 *   where there is no logged-in user and no cookie to send
 */
import axios from 'axios';
import { useQueryClient } from '@tanstack/react-query';

import { apiClient } from '../../apiHelper';
import { useCreate, useDelete, useFetch } from '../react-query';

export type ShareContentScope = 'clinical_only' | 'images_only' | 'both';
export type ShareRedactionProfile = 'identified' | 'anonymized';

/** The three locked channels. `qr_code` transmits nothing: the clinician shows
 *  or prints the code and the patient scans it at the desk. */
export type ShareDeliveryChannel = 'qr_code' | 'whatsapp' | 'email';

/** Fields a patient may be asked for. Named by the server's unlock policy, so the
 *  form is driven by the share rather than hardcoded on this side. */
export type ShareChallengeField = 'date_of_birth' | 'national_id';

export interface ShareChallenge {
  /** Registry key, e.g. `dob_national_id`. Null when the record cannot be
   *  challenged at all, which means the share will be refused. */
  policy: string | null;
  fields: ShareChallengeField[];
}

export interface ExamShare {
  id: string;
  study_instance_uid: string;
  patient_id: string;
  created_by_id: string | null;
  content_scope: ShareContentScope;
  redaction_profile: ShareRedactionProfile;
  delivery_channel: ShareDeliveryChannel;
  unlock_policy: string;
  created_at: string;
  expires_at: string;
  revoked_at: string | null;
  /** Unlocks, not page loads. One, for a single-use link. */
  max_unlocks: number;
  unlock_count: number;
  last_unlocked_at: string | null;
  is_active: boolean;
}

export interface ShareDelivery {
  channel: string;
  /** False for qr_code by design: nothing was transmitted. */
  sent: boolean;
  reason: string | null;
  /** The normalized destination the message went to, when one was used. */
  recipient?: string | null;
}

export interface ShareRecipient {
  phone_number: string | null;
  email: string | null;
  source: 'record' | 'none';
  /** What the patient will be asked for, resolved by the same function that
   *  freezes it onto the share, so the dialog cannot promise the wrong thing. */
  challenge: ShareChallenge;
  /** Which contact field each channel needs, keyed by channel name. */
  contact_fields?: Record<string, string | null>;
}

export interface ExamShareCreated {
  share: ExamShare;
  /** The full link. Returned once, at creation, and never again. */
  url: string;
  expires_at: string;
  challenge?: ShareChallenge;
  delivery?: ShareDelivery;
}

export interface RedactionProfileInfo {
  name: ShareRedactionProfile;
  description: string;
}

// A type alias rather than an interface: apiClient.post takes
// Record<string, unknown>, and only aliases get an implicit index signature.
//
// Note what is absent: no unlock count and no policy. A share is single use, and
// the server picks the strongest challenge the record supports - neither is the
// dialog's to choose.
export type CreateExamSharePayload = {
  content_scope: ShareContentScope;
  redaction_profile: ShareRedactionProfile;
  ttl_hours?: number;
  delivery_channel?: ShareDeliveryChannel;
  recipient_phone?: string;
  recipient_email?: string;
};

export const examShareKeys = {
  list: (studyId: string) => ['exam-shares', studyId],
  profiles: () => ['exam-share-profiles'],
  recipient: (studyId: string) => ['exam-share-recipient', studyId],
};

/* -------------------------------------------------------------------------
 * Clinician side
 * ---------------------------------------------------------------------- */

// apiHelper installs a response interceptor that already returns the envelope,
// so `response.data` is the payload. A second `.data` yields undefined.
export function useExamShares(studyId: string) {
  const fetchShares = async (): Promise<ExamShare[]> => {
    const response = await apiClient.get(`/study_report/${studyId}/shares`);
    return response?.data ?? [];
  };

  return useFetch<ExamShare[]>(fetchShares, examShareKeys.list(studyId), {
    enabled: !!studyId,
    refetchOnWindowFocus: false,
  });
}

export function useRedactionProfiles() {
  const fetchProfiles = async (): Promise<RedactionProfileInfo[]> => {
    const response = await apiClient.get('/study_report/share-profiles');
    return response?.data ?? [];
  };

  return useFetch<RedactionProfileInfo[]>(
    fetchProfiles,
    examShareKeys.profiles(),
    { refetchOnWindowFocus: false, staleTime: Infinity }
  );
}

const NO_RECIPIENT: ShareRecipient = {
  phone_number: null,
  email: null,
  source: 'none',
  challenge: { policy: null, fields: [] },
};

/** The patient's contact details on file plus the challenge she will be asked,
 *  so the dialog can prefill a destination and say what to tell her. */
export function useShareRecipient(studyId: string, enabled: boolean) {
  const fetchRecipient = async (): Promise<ShareRecipient> => {
    const response = await apiClient.get(
      `/study_report/${studyId}/share-recipient`
    );
    return response?.data ?? NO_RECIPIENT;
  };

  return useFetch<ShareRecipient>(
    fetchRecipient,
    examShareKeys.recipient(studyId),
    { enabled: !!studyId && enabled, refetchOnWindowFocus: false }
  );
}

export function useCreateExamShare(
  studyId: string,
  onSuccess?: (created: ExamShareCreated) => void
) {
  const queryClient = useQueryClient();

  const createShare = async (payload: CreateExamSharePayload) => {
    const response = await apiClient.post(
      `/study_report/${studyId}/shares`,
      payload
    );
    return response;
  };

  return useCreate<CreateExamSharePayload>(
    createShare,
    examShareKeys.list(studyId),
    'Could not create the link',
    'Link created',
    (response: any) => {
      queryClient.invalidateQueries({ queryKey: examShareKeys.list(studyId) });
      const created = response?.data as ExamShareCreated | undefined;
      if (created?.url) {
        onSuccess?.(created);
      }
    }
  );
}

export function useRevokeExamShare(studyId: string) {
  const queryClient = useQueryClient();

  const revokeShare = async (shareId: string) => {
    const response = await apiClient.delete(
      `/study_report/shares/${shareId}`,
      {}
    );
    queryClient.invalidateQueries({ queryKey: examShareKeys.list(studyId) });
    return response;
  };

  return useDelete<string>(
    revokeShare,
    examShareKeys.list(studyId),
    'Could not revoke the link',
    'Link revoked'
  );
}

/* -------------------------------------------------------------------------
 * Patient side (public /shared/:token page)
 *
 * A bare axios instance, deliberately: `withCredentials` is off so a clinician
 * testing a link in their own logged-in browser exercises the same
 * unauthenticated path a patient gets. The session token is carried in a
 * header rather than a cookie, so it is never sent automatically.
 * ---------------------------------------------------------------------- */

const SESSION_HEADER = 'X-Share-Session';

const publicClient = axios.create({
  baseURL: axios.defaults.baseURL,
  withCredentials: false,
});

/**
 * What the server says about a link.
 *
 * `unusable` and `unreachable` are kept apart on purpose. The first is a verdict:
 * the link is spent, expired or revoked, and the API deliberately does not say
 * which. The second is the absence of a verdict, and telling a patient on a bad
 * connection that her link was withdrawn would be a lie the page has no basis for.
 */
export type ShareProbe =
  /** `fields` comes from the share's own policy, so a link minted against a
   *  record with no ID on file asks for a date of birth alone. */
  | { state: 'active'; fields: ShareChallengeField[] }
  | { state: 'unusable' }
  | { state: 'unreachable' };

export interface UnlockAnswers {
  date_of_birth: string;
  national_id?: string;
}

export interface UnlockResult {
  session_token: string;
  expires_in: number;
  content_scope: ShareContentScope;
}

/** Ask the server what this link is. Never throws: every outcome is a state. */
export async function probeShare(token: string): Promise<ShareProbe> {
  try {
    const response = await publicClient.get(
      `/public/shares/${encodeURIComponent(token)}`
    );
    const meta = response.data?.data;
    if (!meta?.fields?.length) {
      // No answerable question means the link is not usable, whatever the state
      // field says.
      return { state: 'unusable' };
    }
    return { state: 'active', fields: meta.fields };
  } catch (error: any) {
    // The API answers 404 for every dead link, so a 4xx is the verdict. A 5xx or
    // no response at all is the server or the network failing, which says nothing
    // about the link and must not be shown to the patient as though it did.
    const status = error?.response?.status;
    if (typeof status === 'number' && status >= 400 && status < 500) {
      return { state: 'unusable' };
    }
    return { state: 'unreachable' };
  }
}

/** Spends the link. A success is the only unlock this share will ever allow, so
 *  the caller must hold on to the session it returns. */
export async function unlockShare(
  token: string,
  answers: UnlockAnswers
): Promise<UnlockResult> {
  const response = await publicClient.post(
    `/public/shares/${encodeURIComponent(token)}/unlock`,
    answers
  );
  return response.data?.data;
}

export async function fetchSharedReport(sessionToken: string): Promise<any> {
  const response = await publicClient.get('/public/shares/session/report', {
    headers: { [SESSION_HEADER]: sessionToken },
  });
  return response.data?.data;
}

/**
 * The redacted report as HTML, which is what the page displays.
 *
 * Returned as markup rather than JSON, so the response is the document itself.
 */
export async function fetchSharedReportHtml(
  sessionToken: string,
  locale: string
): Promise<string> {
  const response = await publicClient.get('/public/shares/session/report-html', {
    headers: { [SESSION_HEADER]: sessionToken },
    params: { locale },
    responseType: 'text',
    // Without this axios sniffs the body and hands back a parsed object for
    // anything that happens to look like JSON.
    transformResponse: [(data: string) => data],
  });
  return response.data;
}

/** Redacted PDF for a live session. Returns a Blob for direct download. */
export async function fetchSharedReportPdf(
  sessionToken: string,
  locale: string
): Promise<Blob> {
  const response = await publicClient.get('/public/shares/session/pdf', {
    headers: { [SESSION_HEADER]: sessionToken },
    params: { locale },
    responseType: 'blob',
  });
  return response.data;
}

export async function closeShareSession(sessionToken: string): Promise<void> {
  try {
    await publicClient.post(
      '/public/shares/session/close',
      {},
      { headers: { [SESSION_HEADER]: sessionToken } }
    );
  } catch {
    // Best effort: the session expires on its own regardless.
  }
}
